import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useUpdateBoard } from "@/hooks/useBoard"
import { useToast } from "@/hooks/useToast"

interface EditBoardDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  boardId: string
  initialName: string
  initialDescription: string
}

export function EditBoardDialog({
  open,
  onOpenChange,
  boardId,
  initialName,
  initialDescription,
}: EditBoardDialogProps) {
  const { toast } = useToast()
  const updateBoard = useUpdateBoard()
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState(initialDescription)

  useEffect(() => {
    if (open) {
      setName(initialName)
      setDescription(initialDescription)
    }
  }, [open, initialName, initialDescription])

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        description: "Board name is required",
        variant: "destructive",
      })
      return
    }
    try {
      await updateBoard.mutateAsync({
        id: boardId,
        name: name.trim(),
        description: description.trim() || undefined,
      })
      onOpenChange(false)
      toast({ description: "Board updated successfully" })
    } catch (error: any) {
      toast({
        description: error?.response?.data?.error || "Failed to update board",
        variant: "destructive",
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Board</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter board name"
            />
          </div>
          <div>
            <Label htmlFor="edit-description">Description (optional)</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter board description"
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateBoard.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
